import React, { useCallback, useEffect } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { Content } from '../../components/shared/Content';
import PaymentForm from '../../components/transaction/Payment/PaymentForm';
import { PaymentActionType } from '../../components/transaction/Payment/form/actions';
import paymentReducer from '../../components/transaction/Payment/form/reducer';
import { paymentInitialState } from '../../components/transaction/Payment/form/state';
import useFormContext, { PaymentFormContext } from '../../components/transaction/Payment/form/useFormContext';
import useValidate from '../../components/transaction/Payment/form/validate';
import useAmount from '../../hooks/queryparams/useAmount';
import useQueryParam from '../../hooks/queryparams/useQueryParam';
import usePaymentAccount from '../../hooks/usePaymentAccount';
import useReceiverAccount from '../../hooks/useReceiverAccount';
import { AnalyticsEvents } from '../../services/analytics';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';

import './Payment.scss';

const Payment: React.FC = () => {
    const { handleChange } = useFormContext();
    const { signingStatus } = useSigningContext();

    const title = useQueryParam('title');
    const amount = useAmount();
    const account = usePaymentAccount();
    const receiver = useReceiverAccount();
    const kid = useQueryParam('kid');
    const returnUrl = useQueryParam('returnUrl');

    useEffect(() => {
        if (amount) {
            handleChange({ type: PaymentActionType.AMOUNT, payload: amount });
        }
    }, [handleChange, amount]);
    useEffect(() => {
        if (kid) {
            handleChange({ type: PaymentActionType.SET_KID, payload: { value: kid } });
        }
    }, [handleChange, kid]);
    useEffect(() => {
        if (receiver) {
            handleChange({ type: PaymentActionType.TO_ACCOUNT_SELECTED, payload: receiver });
        }
    }, [handleChange, receiver]);

    const onClearPayment = useCallback(() => {
        if (signingStatus !== 'UNKNOWN' && signingStatus !== 'REJECTED') {
            handleChange({ type: PaymentActionType.RESET });
        }
    }, [handleChange, signingStatus]);

    const onClose = useCallback(() => {
        if (signingStatus !== 'UNKNOWN' && signingStatus !== 'REJECTED') {
            if (returnUrl) {
                window.location.href = returnUrl;
            } else {
                window.history.back();
            }
        }
    }, [signingStatus, returnUrl]);

    useEffect(() => {
        if (signingStatus && signingStatus === 'COMPLETED') {
            setTimeout(onClose, 2500);
        }
    }, [onClose, signingStatus]);

    return (
        <Content className="payment-page">
            <PaymentForm
                account={account ?? undefined}
                title={title}
                onClearPayment={onClearPayment}
                onClose={onClose}
                lockNote={!!kid}
                lockReceiver={!!receiver}
                isInModal={false}
            />
        </Content>
    );
};

export default () => (
    <SigningContextProvider analyticsEvent={AnalyticsEvents.PAYMENT} analyticsCategory={ANALYTICS_CATEGORIES.PAYMENT}>
        <PaymentFormContext
            validateHook={useValidate}
            reducer={paymentReducer}
            initialState={{
                ...paymentInitialState,
                paymentDate: getZonedDate(),
            }}
        >
            <Payment />
        </PaymentFormContext>
    </SigningContextProvider>
);
